import { useState, useMemo } from "react";
import { Calculator, TrendingUp, PiggyBank, CreditCard, Flame } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

function fmt(n: number) {
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/* ── SIP / Compound Calculator ── */
function SIPCalculator() {
  const [monthly, setMonthly] = useState("5000");
  const [rate, setRate] = useState("12");
  const [years, setYears] = useState("10");
  const invested = parseFloat(monthly) * 12 * parseFloat(years);
  const r = parseFloat(rate) / 100 / 12;
  const n = parseFloat(years) * 12;
  const fv = parseFloat(monthly) * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  const gain = fv - invested;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label htmlFor="sip-monthly" className="text-xs text-muted-foreground">Monthly Investment (৳)</Label>
          <Input id="sip-monthly" type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} min="100" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sip-rate" className="text-xs text-muted-foreground">Annual Return (%)</Label>
          <Input id="sip-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} min="1" max="50" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sip-years" className="text-xs text-muted-foreground">Time Period (Years)</Label>
          <Input id="sip-years" type="number" value={years} onChange={(e) => setYears(e.target.value)} min="1" max="40" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2">
        {[
          { label: "Amount Invested", value: `৳${fmt(invested)}`, highlight: false },
          { label: "Estimated Returns", value: `৳${fmt(isNaN(gain) ? 0 : gain)}`, highlight: false },
          { label: "Total Value", value: `৳${fmt(isNaN(fv) ? 0 : fv)}`, highlight: true },
        ].map((item) => (
          <div key={item.label} className={cn("rounded-lg p-3 text-center border", item.highlight ? "bg-primary/10 border-primary/30" : "bg-muted/40 border-border/50")}>
            <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
            <p className={cn("font-bold text-sm", item.highlight ? "text-primary" : "text-foreground")}>{item.value}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">* Estimate only. Not financial advice. Results assume constant returns.</p>
    </div>
  );
}

/* ── EMI / Loan Calculator ── */
function EMICalculator() {
  const [principal, setPrincipal] = useState("500000");
  const [rate, setRate] = useState("9");
  const [tenure, setTenure] = useState("60");
  const r = parseFloat(rate) / 100 / 12;
  const n = parseFloat(tenure);
  const emi = principal && rate && tenure
    ? (parseFloat(principal) * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
    : 0;
  const total = emi * n;
  const interest = total - parseFloat(principal);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label htmlFor="emi-principal" className="text-xs text-muted-foreground">Loan Amount (৳)</Label>
          <Input id="emi-principal" type="number" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="emi-rate" className="text-xs text-muted-foreground">Annual Interest (%)</Label>
          <Input id="emi-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} min="1" max="36" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="emi-tenure" className="text-xs text-muted-foreground">Tenure (Months)</Label>
          <Input id="emi-tenure" type="number" value={tenure} onChange={(e) => setTenure(e.target.value)} min="1" max="360" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2">
        {[
          { label: "Monthly EMI", value: `৳${fmt(isNaN(emi) ? 0 : emi)}`, highlight: true },
          { label: "Total Interest", value: `৳${fmt(isNaN(interest) ? 0 : interest)}`, highlight: false },
          { label: "Total Payment", value: `৳${fmt(isNaN(total) ? 0 : total)}`, highlight: false },
        ].map((item) => (
          <div key={item.label} className={cn("rounded-lg p-3 text-center border", item.highlight ? "bg-primary/10 border-primary/30" : "bg-muted/40 border-border/50")}>
            <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
            <p className={cn("font-bold text-sm", item.highlight ? "text-primary" : "text-foreground")}>{item.value}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">* Estimate only. Not financial advice.</p>
    </div>
  );
}

/* ── Budget 50/30/20 Rule ── */
function BudgetCalculator() {
  const [income, setIncome] = useState("50000");
  const val = parseFloat(income) || 0;
  const needs = val * 0.5;
  const wants = val * 0.3;
  const savings = val * 0.2;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="budget-income" className="text-xs text-muted-foreground">Monthly Net Income (৳)</Label>
        <Input id="budget-income" type="number" value={income} onChange={(e) => setIncome(e.target.value)} className="max-w-xs" />
      </div>
      <div className="space-y-2 pt-2">
        {[
          { label: "50% — Needs", sub: "Rent, food, utilities, transport", value: needs, color: "bg-blue-500/20 border-blue-500/30 text-blue-600 dark:text-blue-400", pct: 50 },
          { label: "30% — Wants", sub: "Dining out, shopping, entertainment", value: wants, color: "bg-amber-500/20 border-amber-500/30 text-amber-600 dark:text-amber-400", pct: 30 },
          { label: "20% — Savings & Debt", sub: "Emergency fund, investments, EMIs", value: savings, color: "bg-green-500/20 border-green-500/30 text-green-600 dark:text-green-400", pct: 20 },
        ].map((item) => (
          <div key={item.label} className={cn("flex items-center justify-between rounded-lg p-3 border", item.color)}>
            <div>
              <p className="text-sm font-semibold text-foreground">{item.label}</p>
              <p className="text-xs text-muted-foreground">{item.sub}</p>
            </div>
            <p className="font-bold text-base">৳{fmt(item.value)}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">* The 50/30/20 rule is a budgeting guideline. Not financial advice.</p>
    </div>
  );
}

/* ── Inflation Impact Calculator ── */
function InflationCalculator() {
  const [amount, setAmount] = useState("100000");
  const [inflRate, setInflRate] = useState("7");
  const [years, setYears] = useState("10");

  const amt = parseFloat(amount) || 0;
  const rate = parseFloat(inflRate) || 0;
  const yrs = parseFloat(years) || 0;

  const yearlyData = useMemo(() => {
    const data: { year: number; nominal: number; real: number }[] = [];
    for (let y = 0; y <= Math.min(yrs, 30); y++) {
      data.push({
        year: y,
        nominal: amt,
        real: amt / Math.pow(1 + rate / 100, y),
      });
    }
    return data;
  }, [amt, rate, yrs]);

  const finalReal = yearlyData[yearlyData.length - 1]?.real || 0;
  const loss = amt - finalReal;
  const lossPct = amt > 0 ? (loss / amt) * 100 : 0;

  // Simple inline bar chart
  const maxVal = amt;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label htmlFor="infl-amount" className="text-xs text-muted-foreground">Current Value (৳)</Label>
          <Input id="infl-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} min="1000" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="infl-rate" className="text-xs text-muted-foreground">Inflation Rate (%)</Label>
          <Input id="infl-rate" type="number" value={inflRate} onChange={(e) => setInflRate(e.target.value)} min="1" max="30" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="infl-years" className="text-xs text-muted-foreground">Time Horizon (Years)</Label>
          <Input id="infl-years" type="number" value={years} onChange={(e) => setYears(e.target.value)} min="1" max="30" />
        </div>
      </div>

      {/* Results summary */}
      <div className="grid grid-cols-3 gap-3 pt-2">
        {[
          { label: "Today's Value", value: `৳${fmt(amt)}`, highlight: false },
          { label: `Real Value in ${yrs}yr`, value: `৳${fmt(finalReal)}`, highlight: true },
          { label: "Purchasing Power Lost", value: `${fmt(lossPct)}%`, highlight: false },
        ].map((item) => (
          <div key={item.label} className={cn("rounded-lg p-3 text-center border", item.highlight ? "bg-destructive/10 border-destructive/30" : "bg-muted/40 border-border/50")}>
            <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
            <p className={cn("font-bold text-sm", item.highlight ? "text-destructive" : "text-foreground")}>{item.value}</p>
          </div>
        ))}
      </div>

      {/* Visual bar chart */}
      <div className="space-y-1.5 pt-2">
        <p className="text-xs font-medium text-muted-foreground mb-2">Purchasing Power Erosion Over Time</p>
        {yearlyData
          .filter((_, i) => i === 0 || i === Math.round(yrs / 4) || i === Math.round(yrs / 2) || i === Math.round(yrs * 3 / 4) || i === yearlyData.length - 1)
          .filter((v, i, arr) => arr.findIndex(a => a.year === v.year) === i) // dedupe
          .map((d) => {
            const pct = maxVal > 0 ? (d.real / maxVal) * 100 : 0;
            return (
              <div key={d.year} className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-12 text-right shrink-0">
                  Yr {d.year}
                </span>
                <div className="flex-1 h-5 bg-muted/40 rounded-full overflow-hidden border border-border/30">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      pct > 70 ? "bg-primary" : pct > 40 ? "bg-amber-500" : "bg-destructive"
                    )}
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  />
                </div>
                <span className="text-[11px] font-medium text-foreground w-20 shrink-0">
                  ৳{fmt(d.real)}
                </span>
              </div>
            );
          })}
      </div>

      <p className="text-[11px] text-muted-foreground">* Shows how inflation erodes the purchasing power of a fixed sum over time. Not financial advice.</p>
    </div>
  );
}

function FinanceCalculators() {
  return (
    <section
      aria-labelledby="calculators-heading"
      className="bg-secondary/20 border-y border-border/50"
    >
      <div className="container mx-auto px-4 py-20 max-w-4xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-4">
            <Calculator className="h-7 w-7 text-primary" aria-hidden="true" />
          </div>
          <h2 id="calculators-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
            Free Interactive Financial Tools
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Instantly plan your savings, estimate loan repayments, measure inflation impact, and build a budget — no sign-up required.
            These tools are built into the Financially Fit platform.
          </p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-6 shadow-sm">
          <Tabs defaultValue="inflation">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="inflation" className="flex items-center gap-1.5 text-xs sm:text-sm">
                <Flame className="h-3.5 w-3.5" aria-hidden="true" />
                Inflation
              </TabsTrigger>
              <TabsTrigger value="sip" className="flex items-center gap-1.5 text-xs sm:text-sm">
                <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
                SIP / Returns
              </TabsTrigger>
              <TabsTrigger value="emi" className="flex items-center gap-1.5 text-xs sm:text-sm">
                <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
                EMI / Loan
              </TabsTrigger>
              <TabsTrigger value="budget" className="flex items-center gap-1.5 text-xs sm:text-sm">
                <PiggyBank className="h-3.5 w-3.5" aria-hidden="true" />
                Budget Rule
              </TabsTrigger>
            </TabsList>
            <TabsContent value="inflation"><InflationCalculator /></TabsContent>
            <TabsContent value="sip"><SIPCalculator /></TabsContent>
            <TabsContent value="emi"><EMICalculator /></TabsContent>
            <TabsContent value="budget"><BudgetCalculator /></TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Want to track your actual finances?{" "}
          <a href="/auth" className="text-primary hover:underline font-medium">
            Create a free account →
          </a>
        </p>
      </div>
    </section>
  );
}

export default FinanceCalculators;
